import { ChangeEvent, useState } from 'react';
import cn from 'classnames';
import { shortMonthsList, yearsList } from '@packages/shared/core/common/constants';
import {
  clampBirthDateObject,
  generateMonthDays,
  getBirthDateDaysList,
  getBirthDateMonthsList,
  getBirthDateYearsList,
  getMaxBirthDateForMinimumAge,
} from '@packages/shared/core/common/utils/generateDates';
import NativeSelect from '../NativeSelect/NativeSelect';
import styles from './DateSelect.module.scss';

type DateObject = {
  month: string;
  day: string;
  year: string;
};

type DateSelectProps = {
  selectClassName?: string;
  selectWrapperClassName?: string;
  dateListClassName?: string;
  groupClassName?: string;
  minimumAge?: number;
  onChange: (value: DateObject) => void;
};

const DateSelect = ({
  groupClassName,
  selectClassName,
  selectWrapperClassName,
  dateListClassName,
  minimumAge,
  onChange,
}: DateSelectProps) => {
  const [date, setDate] = useState({
    month: 'Jan',
    day: '01',
    year: '1990',
  });

  const maxBirthDate = minimumAge ? getMaxBirthDateForMinimumAge(minimumAge) : null;

  const years = maxBirthDate ? getBirthDateYearsList(maxBirthDate) : yearsList;
  const months = maxBirthDate ? getBirthDateMonthsList(maxBirthDate, date.year, shortMonthsList) : shortMonthsList;
  const daysList = maxBirthDate
    ? getBirthDateDaysList(maxBirthDate, date.year, date.month, shortMonthsList)
    : generateMonthDays(date.month, date.year);

  const handleDateChange = (e: ChangeEvent<HTMLSelectElement>) => {
    let newDate = { ...date, [e.target.name]: e.target.value };
    const normalizedDay = String(Number(newDate.day));

    if (maxBirthDate) {
      newDate = clampBirthDateObject({ ...newDate, day: normalizedDay }, maxBirthDate, shortMonthsList);
    } else {
      const days = generateMonthDays(newDate.month, newDate.year);

      if (!days.includes(normalizedDay)) {
        newDate = { ...newDate, day: days[days.length - 1] };
      } else {
        newDate = { ...newDate, day: normalizedDay };
      }
    }

    setDate(newDate);
    onChange(newDate);
  };

  return (
    <div className={cn(styles.selectGroup, groupClassName)}>
      <NativeSelect
        selectClassName={selectClassName}
        wrapperClassName={selectWrapperClassName}
        data={months}
        name="month"
        value={date.month}
        onChange={handleDateChange}
        dateListClassName={dateListClassName}
      />

      <NativeSelect
        selectClassName={selectClassName}
        wrapperClassName={selectWrapperClassName}
        data={daysList}
        name="day"
        value={date.day}
        onChange={handleDateChange}
        dateListClassName={dateListClassName}
      />

      <NativeSelect
        selectClassName={selectClassName}
        wrapperClassName={selectWrapperClassName}
        data={years}
        name="year"
        value={date.year}
        onChange={handleDateChange}
        dateListClassName={dateListClassName}
      />
    </div>
  );
};

export default DateSelect;
